// ARKA — temas MUI v5 (@mui/material) e v4 (@material-ui/core) apontando para os tokens.
// Só o dashboard-admin usa MUI (HistoricList: Card).
import React, { useMemo } from 'react';
import { createTheme as createThemeV5, ThemeProvider as ThemeProviderV5 } from '@mui/material/styles';
import { createTheme as createThemeV4, ThemeProvider as ThemeProviderV4 } from '@material-ui/core/styles';
import { arkaColors, arkaFonts, arkaRadius } from './tokens';
import { useArkaMode } from './ThemeToggle';

function palette(mode) {
  const c = arkaColors[mode];
  return {
    type: mode, // v4
    mode, // v5
    primary: { main: c.accentFill, dark: c.accentStrong, contrastText: c.onAccent },
    secondary: { main: c.text2, contrastText: c.bg },
    success: { main: c.success },
    error: { main: c.danger },
    warning: { main: c.warning },
    info: { main: c.info },
    background: { default: c.bg, paper: c.surface },
    text: { primary: c.text, secondary: c.text2, disabled: c.text3 },
    divider: c.border,
  };
}

const typography = {
  fontFamily: arkaFonts.ui,
  fontSize: 14,
  h1: { fontFamily: arkaFonts.display, fontWeight: 700 },
  h2: { fontFamily: arkaFonts.display, fontWeight: 600 },
  h3: { fontFamily: arkaFonts.display, fontWeight: 600 },
  h4: { fontFamily: arkaFonts.display, fontWeight: 600 },
  h5: { fontFamily: arkaFonts.display, fontWeight: 600 },
  h6: { fontFamily: arkaFonts.display, fontWeight: 600 },
  button: { fontWeight: 600, textTransform: 'none' },
};

export function buildV5(mode) {
  const c = arkaColors[mode];
  return createThemeV5({
    palette: palette(mode),
    typography,
    shape: { borderRadius: arkaRadius.control },
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: { styleOverrides: { root: { borderRadius: arkaRadius.surface, border: `1px solid ${c.border}`, boxShadow: 'none' } } },
      MuiButton: { styleOverrides: { root: { borderRadius: arkaRadius.control, boxShadow: 'none' } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: c.surface2, borderRadius: arkaRadius.control },
          notchedOutline: { borderColor: c.border },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: arkaRadius.pill, fontWeight: 600 } } },
    },
  });
}

export function buildV4(mode) {
  const c = arkaColors[mode];
  return createThemeV4({
    palette: palette(mode),
    typography,
    shape: { borderRadius: arkaRadius.control },
    overrides: {
      MuiPaper: { root: { backgroundImage: 'none' } },
      MuiCard: { root: { borderRadius: arkaRadius.surface, border: `1px solid ${c.border}`, boxShadow: 'none' } },
      MuiButton: { root: { borderRadius: arkaRadius.control } },
    },
  });
}

// Envolve o app nos dois ThemeProviders e troca de tema junto com o data-theme do <html>.
export default function ArkaMuiProvider({ children }) {
  const mode = useArkaMode();
  const v5 = useMemo(() => buildV5(mode), [mode]);
  const v4 = useMemo(() => buildV4(mode), [mode]);
  return (
    <ThemeProviderV4 theme={v4}>
      <ThemeProviderV5 theme={v5}>{children}</ThemeProviderV5>
    </ThemeProviderV4>
  );
}
